import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Chip,
    Divider,
    Grid,
    styled,
    StepConnector
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DrawIcon from '@mui/icons-material/Draw';
import PndRestApi from "src/utils/pnd/PndRestApi";
import {
    ADDRESS_TYPE_ANOTHER_HUB,
    ADDRESS_TYPE_HUB_CUSTOMER,
    ADDRESS_TYPE_MERCHANT,
    ADDRESS_TYPE_MERCHANT_CUSTOMER,
    ADDRESS_TYPE_OWN_HUB,
    OPERATION_TYPE_FOR_PARTIAL_DELIVERY,
    ORDER_TYPE_DROP_OFF
} from "src/utils/pnd/PndConstants";
import { formatDate } from "src/utils/DateUtils";
import SignatureImage from "./SignatureImage";
import moment from 'moment';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.MuiStepConnector-alternativeLabel`]: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.MuiStepConnector-active`]: {
        [`& .MuiStepConnector-line`]: {
            borderColor: '#11998e',
        },
    },
    [`&.MuiStepConnector-completed`]: {
        [`& .MuiStepConnector-line`]: {
            borderColor: '#11998e',
        },
    },
    [`& .MuiStepConnector-line`]: {
        borderColor: '#eaeaf0',
        borderTopWidth: 3,
        borderRadius: 1,
    },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
    color: '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(ownerState.active && {
        color: '#11998e',
    }),
    '& .QontoStepIcon-completedIcon': {
        color: '#11998e',
        zIndex: 1,
        fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
}));

function QontoStepIcon(props) {
    const { active, completed, className } = props;
    return (
        <QontoStepIconRoot ownerState={{ active }} className={className}>
            {completed ? <CheckCircleIcon className="QontoStepIcon-completedIcon" /> : <div className="QontoStepIcon-circle" />}
        </QontoStepIconRoot>
    );
}

const OrderDetailsShippingViewDetailsModal = ({ modalShow, setModalShow, dataInfo }) => {
    const [loading, setLoading] = useState(false);
    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [isOrderItemDetails, setOrderItemDetails] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [addressInfo, setAddressInfo] = useState(null);
    const [rejectedItems, setRejectedItems] = useState(null);
    const EndPoint = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (modalShow && dataInfo?.orderId) {
            fetchAllDetails();
        }
    }, [modalShow, dataInfo]);

    const fetchAllDetails = async () => {
        setLoading(true);
        try {
            await Promise.all([
                getOrderItemWiseDetails(),
                getStatusTrackingData(),
                getAttachmentDetails(),
                getOrderWiseAddressInformation(),
                dataInfo?.orderStatusInfo?.orderStatusId === OPERATION_TYPE_FOR_PARTIAL_DELIVERY ? getRejectedItemDetails() : Promise.resolve()
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getAttachmentDetails = async () => {
        try {
            const response = await PndRestApi.GetOrderAttachmentCount(dataInfo?.orderId);
            if (response.status === 200 && response.data.status === 200) {
                setAttachments(response?.data?.data || []);
            }
        } catch (error) { console.error(error); }
    };

    const getRejectedItemDetails = async () => {
        try {
            const response = await PndRestApi.GetRejectedItemDetails(dataInfo?.orderId);
            if (response.status === 200 && response.data.status === 200) {
                setRejectedItems(response?.data?.data);
            }
        } catch (error) { console.error(error); }
    };

    const getOrderWiseAddressInformation = async () => {
        try {
            const response = await PndRestApi.GetOrderWiseAddressInformation(dataInfo?.orderId);
            if (response.status === 200 && response.data.status === 200) {
                setAddressInfo(response?.data?.data);
            }
        } catch (error) { console.error(error); }
    };

    const getOrderItemWiseDetails = async () => {
        try {
            const response = await PndRestApi.GetOrderItemWiseDetails(dataInfo.orderId);
            if (response.status === 200 && response.data.status === 200) {
                setOrderItemDetails(response?.data?.data || []);
            }
        } catch (error) { console.error(error); }
    };

    const getStatusTrackingData = async () => {
        try {
            const response = await PndRestApi.GetOrderProcessedStatus(dataInfo.orderId);
            if (response.status === 200 && response.data.status === 200) {
                setSteps(response.data.data);
                setActiveStep(response.data.data.length - 1);
            }
        } catch (error) { console.error(error); }
    };

    const handleClose = () => setModalShow(false);

    const getAddressTypeName = (typeId) => {
        switch (typeId) {
            case ADDRESS_TYPE_OWN_HUB: return "Own Hub";
            case ADDRESS_TYPE_MERCHANT: return "Merchant";
            case ADDRESS_TYPE_ANOTHER_HUB: return "Another Hub";
            case ADDRESS_TYPE_HUB_CUSTOMER: return "Hub Customer";
            case ADDRESS_TYPE_MERCHANT_CUSTOMER: return "Merchant Customer";
            default: return "Unknown";
        }
    };

    const order = isOrderItemDetails[0]?.orderInfo || {};
    return (
        <Dialog
            open={modalShow}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
        >
            <DialogTitle sx={{
                p: 2,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 1.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                        Order Lifecycle & History
                    </Typography>
                    <Box
                        component="span"
                        sx={{
                            backgroundColor: order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.colorCode || '#32CA71',
                            color: 'white',
                            fontSize: '0.7rem',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            ml: 1.5,
                            verticalAlign: 'middle',
                            display: 'inline-block',
                            fontWeight: 600,
                            textTransform: 'none'
                        }}
                    >
                        {order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.tagName}
                    </Box>
                    <Chip
                        label={dataInfo?.orderType == 1 ? "Drop Off" : dataInfo?.orderType == 2 ? "Pick Up" : "Pick & Drop"}
                        size="small"
                        sx={{ ml: 2, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, border: '1px solid rgba(255,255,255,0.4)' }}
                    />
                </Box>
                <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                <Grid container spacing={3}>
                    {/* Identification Header */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eef2f6', bgcolor: 'white' }}>
                            <Grid container spacing={2} textAlign="center">
                                <Grid item xs={4} textAlign="left" sx={{ borderRight: '1px solid #f1f5f9' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Order No.</Typography>
                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 800 }}>{dataInfo?.orderNo || '--'}</Typography>
                                </Grid>
                                <Grid item xs={4} textAlign="left" sx={{ borderRight: '1px solid #f1f5f9' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>ERP Order No.</Typography>
                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 800 }}>{dataInfo?.erpOrderNo || '--'}</Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Sales/E-com No</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#32325d' }}>{dataInfo?.salesOrderNo || '--'}</Typography>
                                </Grid>
                                <Grid item xs={4} sx={{ borderRight: '1px solid #f1f5f9' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Invoice No.</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#32325d' }}>{dataInfo?.invoiceNo || 'N/A'}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Timeline */}
                    <Grid item xs={12}>
                        <Box sx={{ py: 2 }}>
                            <Stepper activeStep={activeStep} alternativeLabel connector={<QontoConnector />}>
                                {steps.map((step, index) => (
                                    <Step key={index}>
                                        <StepLabel StepIconComponent={QontoStepIcon}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, d: 'block', color: index === activeStep ? 'primary.main' : 'text.primary' }}>
                                                {step.actionDescription}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                                                {step.hubUserName || step.processedRiderName || '--'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                                                {step.createdOn ? moment(step.createdOn).format('MMM DD, hh:mm A') : ''}
                                            </Typography>
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>
                    </Grid>

                    {/* Address Cards */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 2, height: '100%', borderRadius: '12px', border: '1px solid #eef2f6' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: '#11998e' }}>
                                <LocationOnIcon fontSize="small" sx={{ mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Pickup From: {getAddressTypeName(addressInfo?.fromTypeId)}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>{addressInfo?.pickupAddress || "Not available"}</Typography>
                            {addressInfo?.pickupContactDetails && <Chip label={addressInfo.pickupContactDetails} size="small" variant="outlined" sx={{ borderRadius: 1 }} />}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 2, height: '100%', borderRadius: '12px', border: '1px solid #eef2f6' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: '#32325d' }}>
                                <LocationOnIcon fontSize="small" sx={{ mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Deliver To: {getAddressTypeName(addressInfo?.toTypeId)}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>{addressInfo?.dropOffAddress || "Not available"}</Typography>
                            {addressInfo?.dropOffContactDetails && <Chip label={addressInfo.dropOffContactDetails} size="small" variant="outlined" sx={{ borderRadius: 1 }} />}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 2, height: '100%', borderRadius: '12px', border: '1px solid #eef2f6', bgcolor: '#f1f5f9' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#718096' }}>
                                <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Time Details</Typography>
                            </Box>
                            <Box sx={{ maxHeight: 120, overflow: 'auto', pr: 1 }}>
                                {[
                                    { label: 'Punched', val: dataInfo?.createdDateTime },
                                    { label: 'Requested', val: dataInfo?.requestedDateTimeSlot },
                                    { label: 'Assigned', val: dataInfo?.assignedDateTime },
                                    { label: 'Picked Up', val: dataInfo?.pickedUpDateTime, condition: dataInfo?.orderType != ORDER_TYPE_DROP_OFF },
                                    { label: 'Delivered', val: dataInfo?.completedDateTime }
                                ].map((item, i) => (item.condition !== false) && (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, borderBottom: '1px solid #e2e8f0', pb: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{item.label}:</Typography>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700 }}>{formatDate(item.val) || '--'}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Manifest Table */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, d: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: 4, height: 16, bgcolor: '#11998e', mr: 1, borderRadius: 1 }} />
                            Package Manifest
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #eef2f6', overflow: 'hidden' }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Barcode</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Weight</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Dimensions</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }} align="center">Scan Out</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isOrderItemDetails.map((item, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>{item.boxBarCode}</TableCell>
                                            <TableCell>{item.noOfItems}</TableCell>
                                            <TableCell>{item.weightKg ? `${item.weightKg} Kg` : '--'}</TableCell>
                                            <TableCell>{item.boxDimension || '--'}</TableCell>
                                            <TableCell align="center">
                                                {item.isScannedOut ? <CheckCircleIcon color="success" sx={{ fontSize: 18 }} /> : <CancelIcon color="error" sx={{ fontSize: 18 }} />}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{item.boxDescription || '--'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    {/* Payment & Footer Docs */}
                    <Grid item xs={12} md={7}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid #eef2f6' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'primary.main' }}>
                                <PaymentIcon fontSize="small" sx={{ mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Finance Summary</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: '8px' }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{dataInfo?.orderTotalAmount || '0.00'}</Typography>
                                </Box>
                                <Box textAlign="center">
                                    <Typography variant="caption" color="text.secondary">Collectable</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>{dataInfo?.riderCollectionAmount || '0.00'}</Typography>
                                </Box>
                                <Box textAlign="right">
                                    <Typography variant="caption" color="text.secondary">Mode</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{dataInfo?.paymentMethodInfo?.description || 'N/A'}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <InfoIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.secondary">
                                    <b>Bearer:</b> {dataInfo?.deliveryChargeBearerInfo?.deliveryChargeBearerName || 'N/A'}
                                    <br />
                                    <b> Delivery Instructions:</b> {dataInfo?.orderInstruction || "NIL"}
                                    <br />
                                    <b> Rider Instructions:</b> {dataInfo?.riderDeliveryInstructions || "NIL"}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Paper elevation={0} sx={{ p: 2, border: '1px solid #eef2f6', borderRadius: '12px', textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, d: 'block', mb: 1, textTransform: 'uppercase' }}>Proof of Delivery</Typography>
                                    <Box sx={{ border: '1px dashed #e2e8f0', height: 80, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fcfcfc' }}>
                                        {dataInfo?.customerSignature ? <SignatureImage imageData={dataInfo?.customerSignature} /> : <DrawIcon sx={{ color: 'text.disabled', opacity: 0.2 }} />}
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper elevation={0} sx={{ p: 2, border: '1px solid #eef2f6', borderRadius: '12px' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, d: 'block', mb: 1, textTransform: 'uppercase' }}>Attachments</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {attachments.length > 0 ? attachments.map((item, index) => (
                                            <a key={index} href={`${EndPoint}${item.fileUrl}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: '#11998e', fontWeight: 700 }}>
                                                <AttachFileIcon sx={{ fontSize: 14, mr: 0.5 }} /> File {index + 1}
                                            </a>
                                        )) : <Typography variant="caption" color="text.disabled">No files</Typography>}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc' }}>
                <Typography variant="caption" color="text.disabled">
                    System verified audit history by PickNdrop.ae Management System
                </Typography>
            </Box>
        </Dialog>
    );
};

OrderDetailsShippingViewDetailsModal.propTypes = {
    modalShow: PropTypes.bool.isRequired,
    setModalShow: PropTypes.func.isRequired,
    dataInfo: PropTypes.object
};

export default OrderDetailsShippingViewDetailsModal;
